import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  ServiceAgreementDocument,
  ServiceAgreementDraftPayload,
  ServiceAgreementSubmitPayload,
} from '../types/document'
import type {
  GdprConsentResponse,
  ServiceAgreementListParams,
  SubmitGdprConsentPayload,
} from '../types'

export type { GdprConsentResponse, SubmitGdprConsentPayload } from '../types'

export async function getServiceAgreements(
  params?: ServiceAgreementListParams,
): Promise<ServiceAgreementDocument[]> {
  try {
    const response = await apiClient.get<ServiceAgreementDocument[]>('/api/list_all_documnet', {
      params,
    })
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load service agreements')
  }
}

export async function getServiceAgreementById(id: number): Promise<ServiceAgreementDocument> {
  try {
    const response = await apiClient.get<ServiceAgreementDocument>(`/api/view/${id}`)
    return response.data
  } catch (primaryError) {
    try {
      const response = await apiClient.get<ServiceAgreementDocument>(`/api/gdpr_document/${id}`)
      return response.data
    } catch {
      try {
        const docs = await getServiceAgreements()
        const found = docs.find((doc) => Number(doc.id) === id)
        if (found) return found
      } catch {
        // Ignore fallback errors and throw the original detail failure below.
      }
      // Prefer the original `/api/view` error so 404 descriptions match Angular
      // (`notify(error.error || 'Document not found', 0)`).
      if (primaryError && typeof primaryError === 'object') throw primaryError
      throw new Error(
        extractErrorMessage(primaryError) || 'Document not found',
      )
    }
  }
}

/** Angular `updateDraft` / `discardDraft` — partial POST to `/api/add_document/:id`. */
export async function patchServiceAgreementDraft(
  id: number,
  payload: ServiceAgreementDraftPayload,
): Promise<void> {
  try {
    await apiClient.post(`/api/add_document/${id}`, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save document draft')
  }
}

export async function saveServiceAgreement(
  payload: ServiceAgreementSubmitPayload,
  id?: number,
): Promise<ServiceAgreementDocument> {
  try {
    const url = id ? `/api/add_document/${id}` : '/api/add_document'
    const response = await apiClient.post<ServiceAgreementDocument>(url, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save service agreement')
  }
}

export async function deleteServiceAgreement(id: number): Promise<void> {
  try {
    await apiClient.delete(`/api/gdpr_document/${id}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete service agreement')
  }
}

/** Angular GDPR consent gate: `GET /api/check_consent`. */
export async function checkGdprConsent(): Promise<GdprConsentResponse> {
  try {
    const response = await apiClient.get<GdprConsentResponse>('/api/check_consent')
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load service agreement')
  }
}

/** Angular GDPR consent submit: `POST /api/consent`. */
export async function submitGdprConsent(
  payload: SubmitGdprConsentPayload,
): Promise<GdprConsentResponse> {
  try {
    const response = await apiClient.post<GdprConsentResponse>('/api/consent', payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to submit service agreement acknowledgement')
  }
}
